import { type HttpClient, query, segment } from '../http';

import {
  type CorrectionResult,
  type CorrectSheetPayload,
  type DecisionResult,
  type ListQueueQuery,
  type QueuePage,
  type QueueStats,
  type ReviewCheck,
  type SheetReview,
} from './moderation.types';

/**
 * The queue of sheets asking to enter the public catalogue.
 *
 * Warning: administration routes. An application session is refused on all of
 * them, and every decision is written to the audit log — publishing, sending
 * back and refusing all touch somebody else's work.
 */
export function createModerationApi(http: HttpClient) {
  return {
    /**
     * One page of the queue, and every tab's count.
     *
     * ⚠️ Pending sheets come oldest first, answered ones newest first. A queue
     * is read to find what has waited longest; a history is read to find what
     * just happened.
     */
    queue: (criteria: ListQueueQuery = {}) =>
      http.get<QueuePage>(
        `/admin/moderation/games${query({
          status: criteria.status,
          limit: criteria.limit,
          offset: criteria.offset,
          search: criteria.search,
        })}`,
      ),

    /** The four numbers above the queue. */
    stats: () => http.get<QueueStats>('/admin/moderation/stats'),

    /** Everything the examination screen reads, in one answer. */
    sheet: (id: string) =>
      http.get<SheetReview>(`/admin/moderation/games/${segment(id)}`),

    /**
     * Corrects a sheet while examining it — the same gesture.
     *
     * @returns The fields that actually changed. An empty list matters: a
     * correction nobody made must not be announced to the author.
     */
    correct: (id: string, payload: CorrectSheetPayload) =>
      http.patch<CorrectionResult>(
        `/admin/moderation/games/${segment(id)}`,
        payload,
      ),

    /**
     * Takes a sheet, or puts it back with `null`.
     *
     * ⚠️ A claim, not a lock. It stops two administrators examining the same
     * sheet at once; it stops nobody from deciding.
     */
    assign: (id: string, adminId: string | null) =>
      http.put<void>(`/admin/moderation/games/${segment(id)}/assignment`, {
        adminId,
      }),

    /**
     * Publishes the sheet.
     *
     * @param unchecked The checks left unticked. ⚠️ They do not block — they
     * are written to the log. A box that refuses gets ticked without reading.
     */
    approve: (id: string, unchecked: readonly ReviewCheck[] = []) =>
      http.post<DecisionResult>(
        `/admin/moderation/games/${segment(id)}/approve`,
        { unchecked },
      ),

    /**
     * Sends the sheet back to its author.
     *
     * ⚠️ The motive **and** the message are required. A correction request
     * without a message reads as a refusal, and the sheet never comes back.
     */
    requestChanges: (id: string, reason: string, note: string) =>
      http.post<DecisionResult>(
        `/admin/moderation/games/${segment(id)}/changes`,
        { reason, note },
      ),

    /**
     * Refuses the publication.
     *
     * ⚠️ The sheet is **not** deleted: it goes back to private and stays in its
     * author's library with its sessions and its history.
     */
    reject: (id: string, reason: string, note: string) =>
      http.post<DecisionResult>(
        `/admin/moderation/games/${segment(id)}/reject`,
        { reason, note },
      ),
  };
}
